import { List, Map } from 'immutable';
import { createSelector } from 'reselect';

import { databaseId as databaseIdSelector } from '@Selectors/StateParamsSelectors';
import { userDetails } from '@Containers/UserDetails/UserDetailsSelectors';
import { reactions } from '@Constants';

import { databasesLinksMap, CI_DATABASES, RCS_DATABASES, PU_DATABASES, SUPPLIER_DATABASE_IDS } from './UserDatabasesConstants';

export function databases(state) {
  return userDetails(state)?.get('databases', Map());
}

export const databasesList = createSelector([databases], (dbs) => dbs.valueSeq()?.sortBy((item) => item.get('priority')));

export const getAll = databasesList;

export const getListWithIds = createSelector([databasesList], (dbList) => dbList.map((item) => item.get('id')));

export function getDatabaseById(state, ownProps) {
  const { id } = ownProps;
  const dbs = databases(state);

  return dbs.get(id);
}

/**
 * @ngdoc function
 * @name getDatabaseIdByPrefix
 * @param {object} state - binding props with session state
 * @param {object} ownProps - binding props with database prefix
 * @description get user database ID by prefix. `null` if not found.
 * @returns {string|null} database ID. If not found - `null`
 * */
export function getDatabaseIdByPrefix(state, ownProps) {
  const { prefix } = ownProps;
  if (!prefix) {
    return null;
  }
  const dbs = databases(state);
  const db = dbs.find((item) => String(item.get('prefix'))?.toLowerCase() === String(prefix)?.toLowerCase());
  return db ? db.get('id') : null;
}

export function reaxysDatabaseId(state) {
  return userDetails(state)?.get('reaxysId');
}

// eslint-disable-next-line no-shadow
export const getDefaultDatabase = createSelector([databases, reaxysDatabaseId], (databases, reaxysDatabaseId) =>
  databases.get(reaxysDatabaseId)
);

// eslint-disable-next-line no-shadow
export const getSeparatedDatabases = createSelector([databasesList], (databasesList) => {
  let listOfDBs = List();

  databasesList.forEach((db) => {
    db.get('description')
      .split(/, ?/)
      .forEach((name) => {
        const hubPageURL = db.getIn(['hubPageURLs', name]) || db.getIn(['hubPageURLs', databasesLinksMap[name]], '');
        const tooltip = db.getIn(['tooltips', name]) || db.getIn(['tooltips', databasesLinksMap[name]], '');

        const nextDb = db.set('description', name).set('hubPageURL', hubPageURL).set('tooltip', tooltip);

        listOfDBs = listOfDBs.push(nextDb);
      });
  });

  return listOfDBs;
});

export function isDefaultDatabase(state, ownProps) {
  const { id } = ownProps;

  return reaxysDatabaseId(state) === id;
}

export const getUserSearchContexts = createSelector([databasesList], (dbList) =>
  dbList
    .map((item) => item.get('searchContexts'))
    .flatten(1)
    .groupBy((x) => x.get('serverAlias'))
    .map((x) => x.first())
    .toList()
);

export function getDbName(state, ownProps) {
  const dbs = databases(state);

  return dbs.getIn([ownProps.id, 'name'], '');
}

export function isCIDatabase(state, ownProps) {
  const { id } = ownProps;

  return CI_DATABASES.includes(id);
}

export function isRCSDatabase(state, ownProps) {
  const { id } = ownProps;

  return RCS_DATABASES.includes(id);
}

export function isPUDatabase(state, ownProps) {
  const { id } = ownProps;

  return PU_DATABASES.includes(id);
}

export function supportsReaction(state, ownProps) {
  const db = getDatabaseById(state, ownProps);

  if (!db) {
    return false;
  }

  return db.get('availableContexts')?.some((context) => context.get('serverAlias') === reactions);
}

export function supportsSupplierData(state, ownProps) {
  const { id } = ownProps;

  return SUPPLIER_DATABASE_IDS.indexOf(id) !== -1;
}

export function isReaxysContent(state) {
  const databaseId = databaseIdSelector(state);

  return isDefaultDatabase(state, { id: databaseId });
}
